/**
 * Marketing i18n fragment — changeValidation (/product/change-validation).
 *
 * Claims discipline (docs/ai/fixcontrol-website-claims.md): isolated
 * multi-container validation is pilot / per-tenant opt-in ("where
 * configured" stays mandatory); customer-environment validation runs through
 * the FC Agent and is policy-driven pilot functionality. Per-project strategy
 * selection is described at architecture level only — never as self-service.
 * FixControl never replaces the customer's CI/CD; release stays theirs.
 */

export const changeValidationEn = {
  meta: {
    title: "Change validation — isolated Docker validation and validation in your own environment",
    description:
      "FixControl validates a proposed change in an isolated multi-container environment first and, where configured, continues validation inside your own Kubernetes environment through the FC Agent. Evidence travels into the approval; your existing CI/CD and deployment controls stay authoritative.",
  },
  hero: {
    eyebrow: "Change validation · Pilot",
    title: "A change proves itself",
    highlight: "before it reaches your release process.",
    subtitle:
      "FixControl validates a proposed change in an isolated environment first — and, where configured, continues validation inside your own Kubernetes environment through the FC Agent. The evidence travels into the approval, and your existing CI/CD, Kubernetes and Argo controls stay authoritative. Nothing gets replaced.",
    primaryCta: "Book a demo",
    secondaryCta: "How the FC Agent connects",
    bullet1: "Isolated multi-container validation",
    bullet2: "Evidence recorded on every run",
    bullet3: "Your CI/CD stays in charge",
  },
  flow: {
    eyebrow: "The path of a change",
    title: "From proposal to release, with proof at every step",
    step1: {
      tag: "Change",
      title: "A change is proposed",
      body: "A developer — or FixControl’s AI, preparing a fix from a support ticket — proposes a change for a project. Nothing about it has touched your systems yet.",
    },
    step2: {
      tag: "Validate",
      title: "It runs in isolation first",
      body: "Where configured, FixControl starts the database, cache and background services the application needs — read from your repository’s Compose file as configuration, never executed — in one isolated network without internet access, and runs the tests there.",
    },
    step3: {
      tag: "Evidence",
      title: "Every green run is specific",
      body: "A passed validation records exactly what it passed against: the normalized environment configuration and the exact image versions (digests). And where the proven environment knows how to start the application, passing tests are not enough on their own — the application has to come up and answer, and the run records what was started, on which address and how long it took. If the images or the configuration change, the old evidence no longer counts.",
    },
    step4: {
      tag: "Approve",
      title: "A person decides, with the evidence in front of them",
      body: "The validation result is frozen into the approval. The person deciding sees what ran and against which versions — before anything reaches a repository or a pipeline.",
    },
    step5: {
      tag: "Deliver",
      title: "Your CI/CD takes over",
      body: "The approved change travels as a normal merge request through your existing pipeline. FixControl does not replace your CI/CD — your pipeline’s own checks, approvals and deployment steps stay authoritative.",
    },
    step6: {
      tag: "Release",
      title: "Your deployment controls decide the rollout",
      body: "Where configured, a deployment pause your pipeline already holds — or a paused Argo rollout — opens one more FixControl approval, executed through your CI host’s or Argo’s own primitives. Release stays your process.",
    },
  },
  modes: {
    eyebrow: "Two validation environments",
    title: "Isolated first. Your environment where it matters.",
    description:
      "The two environments answer different questions — they are not the same test run twice.",
    local: {
      title: "Isolated Docker validation",
      body: "Fast and reproducible: a fresh, disposable multi-container environment per run, isolated from production and from the internet, cleaned up afterwards. It answers: does this change build, pass its tests and behave next to the database, cache and services the application needs?",
    },
    remote: {
      title: "Validation in your own environment",
      body: "Where configured through the FC Agent, validation continues inside your own Kubernetes environment before release: real runtime context, your cluster’s dependencies and constraints. It answers: does this change behave in the environment it will actually ship to? Same evidence contract, different question.",
    },
  },
  policy: {
    eyebrow: "Which validation runs",
    title: "Policy-driven, never silent",
    noteTitle: "The combination is a choice, not a coincidence",
    noteBody:
      "Isolated Docker validation is the default lane where multi-container validation is configured. Validation in your own environment is optional and policy-driven: it is enabled per project during pilot onboarding, and FixControl’s validation architecture supports running the isolated lane first and the in-cluster lane after it passes — with a required in-cluster validation satisfied only by in-cluster evidence, never silently by a local run.",
  },
  failure: {
    eyebrow: "When validation or a rollout fails",
    title: "A failure produces evidence, not an automatic retry",
    noteTitle: "Governance stays between analysis and execution",
    noteBody:
      "A failed validation or rollout comes back as structured evidence, not as an automatic change to production. FixControl distinguishes an application failure — a red test, a crash at startup — from an infrastructure failure such as an image that would not pull. An application failure can feed a new AI-prepared revision of the change, which goes through validation and approval again from the start; an infrastructure failure surfaces as a health finding for an operator. In both cases, a person stays between the analysis and the next action.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Validation questions",
    q1: {
      q: "Do both validations always run?",
      a: "No. Isolated Docker validation is the default where multi-container validation is configured. Validation in your own environment is optional and policy-driven, enabled per project during pilot onboarding. Where both are required, each needs its own green result.",
    },
    q2: {
      q: "Does this replace our CI/CD pipeline?",
      a: "No. FixControl validates before your pipeline and governs pauses your pipeline already has. Delivery is a normal merge request; your pipeline’s checks, approvals and deployment steps remain authoritative.",
    },
    q3: {
      q: "What evidence does a validation produce?",
      a: "The normalized environment configuration, the exact image digests it ran against, the executed plan, the result — and, where the proven environment knows how to start the application, the proof that the application actually started and answered. All of it recorded per run and frozen into the approval that relies on it. A changed image or configuration invalidates old evidence.",
    },
    q4: {
      q: "Does a failed rollout mean AI changes production again?",
      a: "No. A failure comes back as structured evidence and analysis. Any new change starts the same controlled loop — validation, evidence, approval — and by default nothing reaches your release process without a recorded human decision.",
    },
    q5: {
      q: "Do we need to expose our cluster for in-cluster validation?",
      a: "No. In-cluster validation runs through the FC Agent: an outbound-only connection, signed single-use operations, and a separate least-privilege identity for the validation runner. Your cluster stays private.",
    },
  },
  related: {
    agent: {
      label: "FC Agent",
      description: "How FixControl reaches private infrastructure — outbound only.",
    },
    deployment: {
      label: "Deployment governance",
      description: "Evidence in, one recorded decision, one promotion.",
    },
    security: {
      label: "Security",
      description: "Isolation, governance, evidence and audit in depth.",
    },
    docs: {
      label: "Validation admin guide",
      description: "The full doc: strategies, the evidence contract, environment settings and failure behaviour.",
    },
  },
  cta: {
    title: "See a change earn its release",
    description:
      "Book a demo and follow one change from proposal through isolated validation, evidence and approval into your own release process.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const changeValidationNl: typeof changeValidationEn = {
  meta: {
    title: "Change-validatie — geïsoleerde Docker-validatie en validatie in je eigen omgeving",
    description:
      "FixControl valideert een voorgestelde wijziging eerst in een geïsoleerde multi-container-omgeving en zet de validatie, waar geconfigureerd, voort binnen je eigen Kubernetes-omgeving via de FC Agent. Bewijs reist mee de goedkeuring in; je bestaande CI/CD- en deployment-controles blijven leidend.",
  },
  hero: {
    eyebrow: "Change-validatie · Pilot",
    title: "Een wijziging bewijst zichzelf",
    highlight: "voordat ze je releaseproces bereikt.",
    subtitle:
      "FixControl valideert een voorgestelde wijziging eerst in een geïsoleerde omgeving — en zet de validatie, waar geconfigureerd, voort binnen je eigen Kubernetes-omgeving via de FC Agent. Het bewijs reist mee de goedkeuring in, en je bestaande CI/CD-, Kubernetes- en Argo-controles blijven leidend. Er wordt niets vervangen.",
    primaryCta: "Boek een demo",
    secondaryCta: "Hoe de FC Agent verbindt",
    bullet1: "Geïsoleerde multi-container-validatie",
    bullet2: "Bewijs vastgelegd bij elke run",
    bullet3: "Je CI/CD blijft de baas",
  },
  flow: {
    eyebrow: "Het pad van een wijziging",
    title: "Van voorstel tot release, met bewijs bij elke stap",
    step1: {
      tag: "Wijziging",
      title: "Een wijziging wordt voorgesteld",
      body: "Een developer — of FixControls AI, die een fix voorbereidt vanuit een supportticket — stelt een wijziging voor een project voor. Nog niets ervan heeft je systemen aangeraakt.",
    },
    step2: {
      tag: "Valideren",
      title: "Ze draait eerst in isolatie",
      body: "Waar geconfigureerd start FixControl de database, cache en achtergrondservices die de applicatie nodig heeft — gelezen uit het Compose-bestand van je repository als configuratie, nooit uitgevoerd — in één geïsoleerd netwerk zonder internettoegang, en draait daar de tests.",
    },
    step3: {
      tag: "Bewijs",
      title: "Elke groene run is specifiek",
      body: "Een geslaagde validatie legt precies vast waartegen ze slaagde: de genormaliseerde omgevingsconfiguratie en de exacte image-versies (digests). En waar de bewezen omgeving weet hoe de applicatie start, zijn geslaagde tests op zichzelf niet genoeg — de applicatie moet opkomen en antwoord geven, en de run legt vast wat er gestart werd, op welk adres en hoe lang het duurde. Veranderen de images of de configuratie, dan telt het oude bewijs niet meer.",
    },
    step4: {
      tag: "Goedkeuren",
      title: "Een mens beslist, met het bewijs voor zich",
      body: "Het validatieresultaat wordt bevroren in de goedkeuring. Degene die beslist ziet wat er draaide en tegen welke versies — voordat er iets een repository of pipeline bereikt.",
    },
    step5: {
      tag: "Leveren",
      title: "Je CI/CD neemt het over",
      body: "De goedgekeurde wijziging reist als een gewone merge request door je bestaande pipeline. FixControl vervangt je CI/CD niet — de eigen checks, goedkeuringen en deploymentstappen van je pipeline blijven leidend.",
    },
    step6: {
      tag: "Release",
      title: "Je deployment-controles beslissen over de rollout",
      body: "Waar geconfigureerd opent een deployment-pauze die je pipeline al vasthoudt — of een gepauzeerde Argo-rollout — nog één FixControl-goedkeuring, uitgevoerd via de eigen primitieven van je CI-host of Argo. De release blijft jouw proces.",
    },
  },
  modes: {
    eyebrow: "Twee validatie-omgevingen",
    title: "Eerst geïsoleerd. Jouw omgeving waar het telt.",
    description:
      "De twee omgevingen beantwoorden verschillende vragen — het is niet dezelfde test die twee keer draait.",
    local: {
      title: "Geïsoleerde Docker-validatie",
      body: "Snel en reproduceerbaar: een verse, wegwerpbare multi-container-omgeving per run, geïsoleerd van productie en van het internet, na afloop opgeruimd. Ze beantwoordt: bouwt deze wijziging, slagen de tests, en gedraagt ze zich naast de database, cache en services die de applicatie nodig heeft?",
    },
    remote: {
      title: "Validatie in je eigen omgeving",
      body: "Waar geconfigureerd via de FC Agent gaat de validatie vóór release verder binnen je eigen Kubernetes-omgeving: echte runtime-context, de afhankelijkheden en beperkingen van jouw cluster. Ze beantwoordt: gedraagt deze wijziging zich in de omgeving waarin ze werkelijk gaat draaien? Zelfde bewijscontract, andere vraag.",
    },
  },
  policy: {
    eyebrow: "Welke validatie draait",
    title: "Beleidsgedreven, nooit stil",
    noteTitle: "De combinatie is een keuze, geen toeval",
    noteBody:
      "Geïsoleerde Docker-validatie is de standaard-lane waar multi-container-validatie is geconfigureerd. Validatie in je eigen omgeving is optioneel en beleidsgedreven: ze wordt per project ingeschakeld tijdens pilot-onboarding, en FixControls validatie-architectuur ondersteunt het eerst draaien van de geïsoleerde lane en daarna, na een groen resultaat, de in-cluster lane — waarbij een vereiste in-cluster-validatie alleen door in-cluster-bewijs wordt vervuld, nooit stilzwijgend door een lokale run.",
  },
  failure: {
    eyebrow: "Als validatie of een rollout faalt",
    title: "Een falen levert bewijs op, geen automatische retry",
    noteTitle: "Governance blijft tussen analyse en uitvoering staan",
    noteBody:
      "Een mislukte validatie of rollout komt terug als gestructureerd bewijs, niet als een automatische wijziging aan productie. FixControl onderscheidt een applicatiefout — een rode test, een crash bij het opstarten — van een infrastructuurfout zoals een image die niet binnengehaald kon worden. Een applicatiefout kan een nieuwe, door AI voorbereide revisie van de wijziging voeden, die van voren af aan door validatie en goedkeuring gaat; een infrastructuurfout verschijnt als health-bevinding voor een beheerder. In beide gevallen blijft er een mens staan tussen de analyse en de volgende actie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over validatie",
    q1: {
      q: "Draaien beide validaties altijd?",
      a: "Nee. Geïsoleerde Docker-validatie is de standaard waar multi-container-validatie is geconfigureerd. Validatie in je eigen omgeving is optioneel en beleidsgedreven, per project ingeschakeld tijdens pilot-onboarding. Waar beide vereist zijn, heeft elk zijn eigen groene resultaat nodig.",
    },
    q2: {
      q: "Vervangt dit onze CI/CD-pipeline?",
      a: "Nee. FixControl valideert vóór je pipeline en governt pauzes die je pipeline al heeft. Levering is een gewone merge request; de checks, goedkeuringen en deploymentstappen van je pipeline blijven leidend.",
    },
    q3: {
      q: "Welk bewijs levert een validatie op?",
      a: "De genormaliseerde omgevingsconfiguratie, de exacte image-digests waartegen ze draaide, het uitgevoerde plan, het resultaat — en, waar de bewezen omgeving weet hoe de applicatie start, het bewijs dat de applicatie echt is opgekomen en antwoord gaf. Alles per run vastgelegd en bevroren in de goedkeuring die erop steunt. Een gewijzigde image of configuratie maakt oud bewijs ongeldig.",
    },
    q4: {
      q: "Betekent een mislukte rollout dat AI opnieuw productie wijzigt?",
      a: "Nee. Een falen komt terug als gestructureerd bewijs en analyse. Elke nieuwe wijziging start dezelfde gecontroleerde lus — validatie, bewijs, goedkeuring — en standaard bereikt niets je releaseproces zonder een vastgelegde menselijke beslissing.",
    },
    q5: {
      q: "Moeten we ons cluster blootstellen voor in-cluster-validatie?",
      a: "Nee. In-cluster-validatie loopt via de FC Agent: een uitsluitend uitgaande verbinding, getekende single-use operaties, en een aparte least-privilege-identiteit voor de validatie-runner. Je cluster blijft privé.",
    },
  },
  related: {
    agent: {
      label: "FC Agent",
      description: "Hoe FixControl private infrastructuur bereikt — alleen uitgaand.",
    },
    deployment: {
      label: "Deployment-governance",
      description: "Bewijs erin, één vastgelegde beslissing, één promotie.",
    },
    security: {
      label: "Security",
      description: "Isolatie, governance, bewijs en audit in de diepte.",
    },
    docs: {
      label: "Validatie-beheerdocumentatie",
      description: "De volledige doc: strategieën, het bewijscontract, omgevingsinstellingen en failure-gedrag.",
    },
  },
  cta: {
    title: "Zie een wijziging haar release verdienen",
    description:
      "Boek een demo en volg één wijziging van voorstel via geïsoleerde validatie, bewijs en goedkeuring tot in je eigen releaseproces.",
    primary: "Boek een demo",
    secondary: "Praat met ons",
  },
};
